import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAppDispatch, useAppSelector } from '@redux/hooks';
import {
 identityLoginStart,
 identityVerify2FAStart,
 identityGenerate2FACodeStart,
 identityGoogleStart,
 identityMicrosoftStart,
 identityForgotPasswordStart,
 identityResetPasswordStart,
} from '@redux/identity/identitySlice';
import { TwoFAMethod } from '@redux/identity/identityTypes';
import { FormValues } from '../../types/types';
import { getLastConnectionKey } from '../../utils/authTokens';

export const useLoginForm = () => {
 const dispatch = useAppDispatch();
 const navigate = useNavigate();
 const { t, i18n } = useTranslation();
 const languages = i18n?.options?.resources || {};

 const [passwordVisible, setPasswordVisible] = useState(false);
 const [step, setStep] = useState(0);
 const [errorMessage, setErrorMessage] = useState<string | undefined>(
  undefined,
 );
 const [selectedMethod, setSelectedMethod] = useState<TwoFAMethod | ''>('');
 const [code, setCode] = useState('');
 const [codeGenerated, setCodeGenerated] = useState(false);
 const [forgotEmail, setForgotEmail] = useState('');
 const [resetCode, setResetCode] = useState('');
 const [newPassword, setNewPassword] = useState('');
 const [forgotPasswordMessage, setForgotPasswordMessage] = useState('');

 const {
  register,
  handleSubmit,
  watch,
  setValue,
  formState: { errors },
 } = useForm<FormValues>({
  defaultValues: {
   email: '',
   password: '',
  },
 });

 const { error, loading, success, login, uri } = useAppSelector(
  (state) => state.identity,
 );
 const forgotPasswordLoading = loading?.identityForgotPassword === true;
 const resetPasswordLoading = loading?.identityResetPassword === true;

 const emailRegister = register('email', {
  required: t('login.fieldRequired'),
 });
 const passwordRegister = register('password', {
  required: t('login.fieldRequired'),
 });

 const handleEmailChange = (e: React.ChangeEvent<HTMLInputElement>) => {
  emailRegister.onChange(e);
  setErrorMessage('');
 };

 const handlePasswordChange = (e: React.ChangeEvent<HTMLInputElement>) => {
  passwordRegister.onChange(e);
  setErrorMessage('');
 };

 const onSubmit = (data: FormValues) => {
  dispatch(
   identityLoginStart({
    email: data.email,
    password: data.password,
    connectionKey: getLastConnectionKey(),
   }),
  );
 };

 useEffect(() => {
  if (success?.identityLogin) {
   if (login?.access_token) {
    navigate('/results');
   } else if (login?.requires2FA && login?.methods2FA) {
    setStep(1);
   }
  }
 }, [success, login, navigate]);

 useEffect(() => {
  if (error?.identityLogin) {
   setErrorMessage(error.identityLogin.title || '');
  }
  if (error?.identityVerify2FA) {
   setErrorMessage(error.identityVerify2FA.title || '');
  }
  if (error?.identityGenerate2FACode) {
   setErrorMessage(error.identityGenerate2FACode.title || '');
  }
  if (error?.identityForgotPassword) {
   setErrorMessage(error.identityForgotPassword.title || '');
  }
  if (error?.identityResetPassword) {
   setErrorMessage(error.identityResetPassword.title || '');
  }
 }, [error]);

 useEffect(() => {
  if (success?.identityGenerate2FACode) {
   setCodeGenerated(true);
  }
  if (success?.identityVerify2FA) {
   if (login?.access_token) {
    navigate('/results');
   }
  }
  if (success?.identityGoogle || success?.identityMicrosoft) {
   console.log(uri);
   navigate(uri || '');
  }
 }, [success, login, navigate]);

 useEffect(() => {
  if (success?.identityForgotPassword) {
   setForgotPasswordMessage(t('login.resetCodeSentMessage'));
   setErrorMessage('');
   setStep(3);
  }
  if (success?.identityResetPassword) {
   handleBackToLogin();
  }
 }, [success]);

 const handleMethodChange = (method: TwoFAMethod) => {
  setSelectedMethod(method);
  setCode('');
  setErrorMessage('');
  setCodeGenerated(false);
  if (method === 3) {
   setCodeGenerated(true);
  }
 };

 const handleCodeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
  setCode(e.target.value);
  setErrorMessage('');
 };

 const handleGenerateCode = () => {
  if (!selectedMethod || !login?.sessionId) {
   setErrorMessage(t('login.pleaseSelectMethod'));
   return;
  }
  if (selectedMethod === 1 || selectedMethod === 2) {
   dispatch(
    identityGenerate2FACodeStart({
     SessionId: login.sessionId,
     Method: selectedMethod,
    }),
   );
  }
 };

 const handle2FASubmit = () => {
  if (!selectedMethod || !login?.sessionId) {
   setErrorMessage(t('login.pleaseSelectMethod'));
   return;
  }
  if ((selectedMethod === 1 || selectedMethod === 2) && !codeGenerated) {
   handleGenerateCode();
   return;
  }
  if (!code) {
   setErrorMessage(t('login.pleaseEnterCode'));
   return;
  }
  dispatch(
   identityVerify2FAStart({
    SessionId: login.sessionId,
    Method: selectedMethod,
    Code: code,
   }),
  );
 };

 const handleForgotPasswordClick = () => {
  setStep(2);
  setForgotEmail((watch('email') || '').trim());
  setResetCode('');
  setNewPassword('');
  setForgotPasswordMessage('');
  setErrorMessage('');
 };

 const handleForgotEmailChange = (e: React.ChangeEvent<HTMLInputElement>) => {
  setForgotEmail(e.target.value);
  setErrorMessage('');
 };

 const handleResetCodeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
  setResetCode(e.target.value);
  setErrorMessage('');
 };

 const handleNewPasswordChange = (e: React.ChangeEvent<HTMLInputElement>) => {
  setNewPassword(e.target.value);
  setErrorMessage('');
 };

 const handleForgotPasswordSubmit = () => {
  if (!forgotEmail.trim()) {
   setErrorMessage(t('login.emailRequired'));
   return;
  }

  setErrorMessage('');
  dispatch(
   identityForgotPasswordStart({
    Email: forgotEmail.trim(),
   }),
  );
 };

 const handleResetPasswordSubmit = () => {
  if (!forgotEmail.trim()) {
   setErrorMessage(t('login.emailRequired'));
   return;
  }
  if (!resetCode.trim()) {
   setErrorMessage(t('login.codeRequired'));
   return;
  }
  if (!newPassword) {
   setErrorMessage(t('login.newPasswordRequired'));
   return;
  }

  setErrorMessage('');
  dispatch(
   identityResetPasswordStart({
    Email: forgotEmail.trim(),
    ResetCode: resetCode.trim(),
    NewPassword: newPassword,
   }),
  );
 };

 const handleFormSubmit = (event: React.FormEvent<HTMLFormElement>) => {
  event.preventDefault();

  if (step === 0) {
   void handleSubmit(onSubmit)(event);
   return;
  }

  if (step === 1) {
   handle2FASubmit();
   return;
  }

  if (step === 2) {
   handleForgotPasswordSubmit();
   return;
  }

  if (step === 3) {
   handleResetPasswordSubmit();
  }
 };

 const handleBackToLogin = () => {
  setStep(0);
  setSelectedMethod('');
  setCode('');
  setErrorMessage('');
  setCodeGenerated(false);
  setForgotEmail('');
  setResetCode('');
  setNewPassword('');
  setForgotPasswordMessage('');
 };

 const handleTogglePasswordVisibility = () => {
  setPasswordVisible(!passwordVisible);
 };

 const handleLanguageChange = (language: string) => {
  i18n.changeLanguage(language);
 };

 const handleDevLogin = () => {
  if (import.meta.env.DEV) {
   setValue('email', 'daniil.mikenas@gmail.com');
   setValue('password', 'Ux6kUQjzrZHxAg1d');
  }
 };

 const handleLoginWith = (type: 'google' | 'microsoft') => {
  const originUrl = window.location.origin;
  if (type === 'google') {
   dispatch(
    identityGoogleStart({
     redirectUri: `${originUrl}/google-callback`,
    }),
   );
  } else if (type === 'microsoft') {
   dispatch(
    identityMicrosoftStart({
     redirectUri: `${originUrl}/microsoft-callback`,
    }),
   );
  }
 };

 const availableMethods = login?.methods2FA
  ? Object.entries(login.methods2FA).map(([key, value]) => ({
     method: Number(key) as TwoFAMethod,
     label: value,
    }))
  : [];

 return {
  passwordVisible,
  step,
  errorMessage,
  selectedMethod,
  code,
  codeGenerated,
  forgotEmail,
  resetCode,
  newPassword,
  forgotPasswordMessage,
  forgotPasswordLoading,
  resetPasswordLoading,
  emailRegister,
  passwordRegister,
  handleSubmit,
  handleFormSubmit,
  watch,
  setValue,
  errors,
  loading,
  languages,
  i18n,
  t,
  handleEmailChange,
  handlePasswordChange,
  onSubmit,
  handleMethodChange,
  handleCodeChange,
  handleGenerateCode,
  handle2FASubmit,
  handleBackToLogin,
  handleTogglePasswordVisibility,
  handleLanguageChange,
  handleForgotPasswordClick,
  handleForgotEmailChange,
  handleResetCodeChange,
  handleNewPasswordChange,
  handleForgotPasswordSubmit,
  handleResetPasswordSubmit,
  availableMethods,
  handleLoginWith,
  handleDevLogin,
 };
};

